import { useEffect, useRef, useState } from 'react';
import { useAperturaAnimada } from '../../hooks/useAperturaAnimada';
import { faltaParaElCierre, type PromoActiva } from '../../lib/promoDeTemporada';

/**
 * Banner de la promo de temporada (Cyber Monday…): logo, "40% OFF" y cuenta regresiva,
 * como el de la landing (ADR-2026-11-02-banner-cyber-monday-como-la-landing).
 *
 * `fotos` suma las creaciones de alrededor, como en la landing. Sólo en el catálogo: en
 * el teléfono suman ~330px de alto, y en un checkout eso baja el formulario de pago.
 *
 * `activa` en `null` lo saca con su transición. El cupón se pide después de montar la
 * pantalla, así que en la práctica siempre entra cuando llega.
 *
 * `forma`: `columna` (teléfono) apila logo, % y reloj; `fila` (desktop) pone el logo a
 * la izquierda. No aclara "el primer mes" ni en qué planes aplica: lo dicen los precios
 * de abajo (tachado y "el primer mes"), y repetido en el banner sobraba.
 */
export function BannerDePromo({
  activa,
  forma,
  fotos = false,
  className = '',
}: {
  activa: PromoActiva | null;
  forma: 'columna' | 'fila';
  fotos?: boolean;
  className?: string;
}) {
  const { montado, visible } = useAperturaAnimada(!!activa, 150);
  // Mientras sale, `activa` ya es null: se dibuja la última que hubo.
  const ultima = useRef(activa);
  if (activa) ultima.current = activa;
  const promo = activa ?? ultima.current;
  if (!montado || !promo) return null;

  const { evento, descuento } = promo;
  const enFila = forma === 'fila';
  // El aire que dejan las fotos: abajo en columna (el abanico), a los costados en fila.
  const aire = !fotos
    ? 'px-5 py-5'
    : enFila
      ? 'px-[22%] py-12'
      : // La foto del medio asoma ~85px (335px de ancho) a ~100px (430px): con 140 quedan
        // entre 40 y 55px de aire bajo el reloj.
        'px-5 pb-[140px] pt-6';

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg ${aire} text-white shadow-[0_20px_40px_-24px_rgba(1,20,90,0.8)] ring-1 ring-white/20 transition-[opacity,transform,filter] ease-out ${
        visible
          ? 'opacity-100 duration-300 motion-safe:translate-y-0 motion-safe:blur-0'
          : 'opacity-0 duration-150 motion-safe:translate-y-3 motion-safe:blur-[4px]'
      } ${className}`}
      style={{ background: evento.fondo }}
    >
      {fotos
        ? FOTOS.filter((f) => enFila || f.columna).map((f) => (
            <figure
              key={f.src}
              aria-hidden="true"
              className={`absolute m-0 aspect-[3/4] overflow-hidden rounded-lg shadow-[0_18px_36px_-14px_rgba(2,6,40,0.9)] ring-[3px] ring-white/90 ${
                enFila ? `w-[9.5%] ${f.fila}` : `max-w-[150px] ${f.columna}`
              }`}
            >
              <img
                src={f.src}
                alt=""
                loading="lazy"
                decoding="async"
                draggable={false}
                className="h-full w-full object-cover"
              />
            </figure>
          ))
        : null}

      <div
        className={`relative z-20 flex items-center ${
          enFila ? 'flex-row justify-center gap-6' : 'flex-col gap-3 text-center'
        }`}
      >
        <img
          src={evento.logo}
          alt={evento.nombre}
          width={500}
          height={400}
          draggable={false}
          className={`w-auto shrink-0 select-none object-contain drop-shadow-[0_10px_18px_rgba(2,6,40,0.45)] ${
            enFila ? 'h-24' : 'h-20'
          }`}
        />
        <div className={`flex min-w-0 flex-col ${enFila ? 'items-start' : 'items-center'}`}>
          <p className="text-[44px] font-extrabold leading-none tracking-[-0.03em] tabular-nums">
            {descuento}% OFF
          </p>
          <CuentaRegresiva
            hasta={evento.cuentaHasta}
            color={evento.colorDeCifras}
            enFila={enFila}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Creaciones de Atlas alrededor del texto, las mismas de la landing.
 *
 * En fila (desktop) van tres a cada costado, en espejo: una afuera, centrada en alto, y
 * dos hacia adentro que asoman una por arriba y otra por abajo.
 *
 * En columna (teléfono) van sólo abajo, en abanico: tres encimadas, la del medio más
 * grande y adelante. Asoman poco (el `translate-y` es sobre su propio alto, así que
 * escala con el ancho del teléfono) para que el banner no quede alto. Arriba no llevan:
 * empujaban el "40% OFF" lejos del comienzo del banner. Las que no tienen `columna` no
 * se dibujan en el teléfono.
 */
const FOTOS: { src: string; columna?: string; fila: string }[] = [
  {
    src: '/images/promos/fotos/pelo-al-sol.webp',
    columna: 'z-0 bottom-0 left-[9%] w-[31%] translate-y-[60%] -rotate-[10deg]',
    fila: 'z-0 left-[1.5%] top-1/2 -translate-y-1/2 -rotate-[6deg]',
  },
  {
    src: '/images/promos/fotos/zapa-flotando.webp',
    fila: 'z-10 left-[11%] -top-[18%] rotate-[5deg]',
  },
  {
    src: '/images/promos/fotos/silla-web.webp',
    fila: 'z-10 left-[12%] -bottom-[22%] -rotate-[4deg]',
  },
  {
    src: '/images/promos/fotos/boisere-escarlata.webp',
    columna: 'z-10 bottom-0 left-1/2 w-[36%] -translate-x-1/2 translate-y-[48%] rotate-[2deg]',
    fila: 'z-0 right-[1.5%] top-1/2 -translate-y-1/2 rotate-[6deg]',
  },
  {
    src: '/images/promos/fotos/shampoo-web.webp',
    columna: 'z-0 bottom-0 right-[9%] w-[31%] translate-y-[60%] rotate-[10deg]',
    fila: 'z-10 right-[11%] -top-[18%] -rotate-[5deg]',
  },
  {
    src: '/images/promos/fotos/a-caballo.webp',
    fila: 'z-10 right-[12%] -bottom-[22%] rotate-[4deg]',
  },
];

/** La promo de un catálogo: la de la primera card que la lleva, o `null` si ninguna. */
export function promoDeLosPlanes(plans: { promo?: PromoActiva }[]): {
  activa: PromoActiva | null;
} {
  return { activa: plans.find((p) => p.promo)?.promo ?? null };
}

const dos = (n: number) => String(n).padStart(2, '0');

function CuentaRegresiva({
  hasta,
  color,
  enFila,
}: {
  hasta: Date;
  color: string;
  enFila: boolean;
}) {
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setAhora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const { dias, horas, minutos, segundos } = faltaParaElCierre(ahora, hasta);

  return (
    // `role="timer"` no anuncia cada segundo (su aria-live es off): el lector lo lee una
    // vez, al llegar.
    <div
      role="timer"
      aria-label="Tiempo que falta para que termine la promo"
      className={`mt-3 flex items-center gap-2 ${enFila ? 'flex-row' : 'flex-col'}`}
    >
      <span className="text-[12px] font-semibold text-white/80">Termina en</span>
      <span className="flex gap-1.5">
        {dias > 0 && <Unidad valor={String(dias)} nombre={dias === 1 ? 'día' : 'días'} color={color} />}
        <Unidad valor={dos(horas)} nombre="h" color={color} />
        <Unidad valor={dos(minutos)} nombre="min" color={color} />
        <Unidad valor={dos(segundos)} nombre="seg" color={color} />
      </span>
    </div>
  );
}

function Unidad({ valor, nombre, color }: { valor: string; nombre: string; color: string }) {
  return (
    <span className="flex min-w-[2.75rem] flex-col items-center rounded-md bg-white px-1.5 pb-1 pt-1.5 shadow-[0_8px_18px_-10px_rgba(2,6,40,0.7)]">
      <span className="text-[20px] font-extrabold leading-none tabular-nums" style={{ color }}>
        {valor}
      </span>
      <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-neutral-500">
        {nombre}
      </span>
    </span>
  );
}
